import { formatDateNoTimezone, parseDateAsLocal } from "@/lib/utils";
import {
  EMPTY_FILTER_VALUE,
  NOT_BLANK_FILTER_VALUE,
} from "./dashboard-column-filter-match";

export const DATE_FILTER_FROM_PREFIX = "from:";
export const DATE_FILTER_TO_PREFIX = "to:";
export const DATE_FILTER_BLANK = "blank";

export type DateRangeFilterBounds = {
  from: string | null;
  to: string | null;
  includeBlank?: boolean;
  includeNotBlank?: boolean;
};

/** True when a column should use from/to date pickers instead of value checkboxes. */
export function isDateColumnAccessorKey(accessorKey: string): boolean {
  return (
    accessorKey.endsWith("_date") ||
    accessorKey.endsWith("_at") ||
    accessorKey.endsWith("Date")
  );
}

export function resolveColumnFilterType(
  accessorKey: string,
  filterType?: "values" | "dateRange",
): "values" | "dateRange" {
  if (filterType === "dateRange") return "dateRange";
  if (filterType === "values") return "values";
  return isDateColumnAccessorKey(accessorKey) ? "dateRange" : "values";
}

export function parseDateRangeFilterValue(
  values: string[],
): DateRangeFilterBounds {
  let from: string | null = null;
  let to: string | null = null;
  let includeBlank = false;
  let includeNotBlank = false;
  for (const value of values) {
    if (value === DATE_FILTER_BLANK || value === EMPTY_FILTER_VALUE) {
      includeBlank = true;
    } else if (value === NOT_BLANK_FILTER_VALUE) {
      includeNotBlank = true;
    } else if (value.startsWith(DATE_FILTER_FROM_PREFIX)) {
      from = value.slice(DATE_FILTER_FROM_PREFIX.length);
    } else if (value.startsWith(DATE_FILTER_TO_PREFIX)) {
      to = value.slice(DATE_FILTER_TO_PREFIX.length);
    }
  }
  return { from, to, includeBlank, includeNotBlank };
}

export function buildDateRangeFilterValue(
  from: string | null,
  to: string | null,
  includeBlank = false,
  includeNotBlank = false,
): string[] | undefined {
  const values: string[] = [];
  if (from) {
    values.push(`${DATE_FILTER_FROM_PREFIX}${from}`);
  }
  if (to) {
    values.push(`${DATE_FILTER_TO_PREFIX}${to}`);
  }
  if (includeBlank) {
    values.push(DATE_FILTER_BLANK);
  }
  if (includeNotBlank) {
    values.push(NOT_BLANK_FILTER_VALUE);
  }
  return values.length > 0 ? values : undefined;
}

function endOfDay(date: Date): Date {
  const result = new Date(date);
  result.setHours(23, 59, 59, 999);
  return result;
}

function toComparableDate(value: string | null | undefined): number | null {
  if (!value?.trim()) return null;
  const localDate = parseDateAsLocal(value);
  if (localDate) return localDate.getTime();
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed.getTime();
}

export function matchesDateRangeFilter(
  rawDateValue: string | null | undefined,
  bounds: DateRangeFilterBounds,
): boolean {
  const cellTime = toComparableDate(rawDateValue);
  if (cellTime === null) {
    return Boolean(bounds.includeBlank);
  }
  // A dated cell satisfies "not blank" on its own. Within one column, selected
  // options are OR'd, so not-blank also keeps dates outside a chosen range.
  if (bounds.includeNotBlank) {
    return true;
  }

  const hasRange = Boolean(bounds.from || bounds.to);
  if (!hasRange) {
    return !bounds.includeBlank;
  }

  if (bounds.from) {
    const fromDate = parseDateAsLocal(bounds.from);
    if (fromDate && cellTime < fromDate.getTime()) {
      return false;
    }
  }

  if (bounds.to) {
    const toDate = parseDateAsLocal(bounds.to);
    if (toDate && cellTime > endOfDay(toDate).getTime()) {
      return false;
    }
  }

  return true;
}

export function formatDateRangeFilterLabel(
  bounds: DateRangeFilterBounds,
): string {
  const fromLabel = bounds.from ? formatDateNoTimezone(bounds.from) : null;
  const toLabel = bounds.to ? formatDateNoTimezone(bounds.to) : null;
  let rangeLabel = "";
  if (fromLabel && toLabel) {
    rangeLabel = `${fromLabel} – ${toLabel}`;
  } else if (fromLabel) {
    rangeLabel = `from ${fromLabel}`;
  } else if (toLabel) {
    rangeLabel = `through ${toLabel}`;
  }
  const presence = [
    bounds.includeBlank ? "Blank" : null,
    bounds.includeNotBlank ? "Not blank" : null,
  ]
    .filter((label): label is string => label != null)
    .join(" or ");
  if (presence && rangeLabel) {
    return `${rangeLabel} or ${presence}`;
  }
  if (presence) {
    return presence;
  }
  return rangeLabel;
}

export function defaultDateFilterValue<T extends object, K extends string>(
  row: T,
  accessorKey: K,
  getValue: (row: T, accessorKey: K) => string,
): string {
  const record = row as Record<string, unknown>;
  const raw = record[accessorKey];
  if (typeof raw === "string" && raw.trim()) {
    return raw;
  }
  return getValue(row, accessorKey);
}
